import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Divider from 'components/Divider';
import { GraniteSelect } from 'components/Select/Select';
import { GraniteLabel } from 'components/V2/Label/GraniteLabel';

import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { ReactComponent as Pin } from 'assets/images/pin.svg';
import clsx from 'clsx';
import {
  ProductOfferingType,
  ProductType,
} from 'screens/SelectServices/schemas';
import IconButton from 'components/IconButton';
import { motion } from 'framer-motion';
import { formatCurrency } from 'shared/util/formatCurrency';
import { useSingleServiceManager } from 'context/SingleServiceManagerContext';
import { useCartContext } from '../CartProvider';
import { observer } from 'mobx-react-lite';
import { AddToCartButton } from './AddToCartButton';
import { StarlinkServiceSummary } from './StarlinkServiceSummary';
import RadioButton from 'components/RadioButton/RadioButton';
import { FieldTooltip } from 'components/FieldTooltip/FieldTooltip';

const mainServiceOptions = [
  {
    label: 'Primary (500 GB)',
    value: 'PRIMARY',
  },
  {
    label: 'Backup (50 GB)',
    value: 'BACKUP',
  },
];

const equipmentOptions = [
  {
    label: 'Mini ($561)',
    value: 'mini_bundle',
  },
  {
    label: 'Enterprise ($561)',
    value: 'enterprise_bundle',
  },
  {
    label: 'HPERF ($1,499)',
    value: 'hperf_bundle',
  },
];

interface StarlinkOfferSettingsProps {
  data: {
    address: string;
    id: string;
    offerings: ProductOfferingType[];
    type?: string;
  };
  isPinnedSummary?: boolean;
  classname?: string;
}

export const StarlinkOfferSettings: React.FC<StarlinkOfferSettingsProps> =
  observer(({ data, isPinnedSummary }) => {
    const {
      pinService,
      unpinService,
      selectedValuesMap,
      updateSelectedValues,
      setProductMap,
    } = useSingleServiceManager();

    const {
      isStarlinkItemAlreadyInCart,
      addStarlinkCartItem,
      removeStarlinkCartItem,
    } = useCartContext();

    const selectedValues = useMemo(() => {
      const compositeKey = `${data.id}_${data.type}_${!!isPinnedSummary}`;

      return selectedValuesMap[compositeKey] || {};
    }, [data.id, data.type, isPinnedSummary, selectedValuesMap]);

    //@ts-expect-error err
    const normData = data?.offerings?.map((d: ProductType) => ({
      id: d.id,
      tier: d.service_level_tier,
      bundle: 'enterprise_bundle',
      installation: d.formal_starlink_pricing?.installation,
      installation_type: selectedValues
        ? selectedValues.installation_type
        : 'granite',
      activation: d.formal_starlink_pricing?.activation,
      bundle_price: d.formal_starlink_pricing?.enterprise_bundle,
      hperf_price: d.formal_starlink_pricing?.hperf_bundle,
      total_mrc: d.formal_starlink_pricing?.total_mrc,
      pricing: d.formal_starlink_pricing,
      raw: d,
    }));

    const tierMap = useMemo(() => {
      const map: Record<string, (typeof normData)[0]> = {};
      normData.forEach((item) => {
        //@ts-expect-error err
        map[item.tier] = item;
      });
      return map;
    }, [normData]);

    useEffect(() => {
      const compositeKey = `${data.id}_${data.type}_${!!isPinnedSummary}`;
      const selected = selectedValuesMap[compositeKey];

      if (!selected) {
        updateSelectedValues(data.id, data.type!, !!isPinnedSummary, {
          service_level_tier: 'PRIMARY',
          bundle: 'enterprise_bundle',
          installation_type: 'granite',
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectChange = (key: string, value: string) => {
      updateSelectedValues(data.id, data.type!, !!isPinnedSummary, {
        ...selectedValues,
        [key]: value,
      });
    };

    const summaryData = useMemo(() => {
      const tier = selectedValues.service_level_tier;
      return tierMap[tier]?.raw ?? {};
    }, [selectedValues, tierMap]);

    const handlePin = useCallback(() => {
      const defaultServiceKey = `${data.id}_${data.type}_false`;
      updateSelectedValues(
        data.id,
        data.type!,
        true,
        selectedValuesMap[defaultServiceKey] || {},
      );
      pinService(data.id, data.type!, {
        ...data,
      });
    }, [data, pinService, selectedValuesMap, updateSelectedValues]);

    const handleUnpin = useCallback(() => {
      unpinService(data?.id, data?.type ?? '');
      setProductMap((current) => {
        return {
          ...current,
        };
      });
    }, [data.id, data.type, setProductMap, unpinService]);

    const handleAddToCart = useCallback(async () => {
      if (
        isStarlinkItemAlreadyInCart({
          ...summaryData,
          bundle: selectedValues.bundle,
        })
      ) {
        await removeStarlinkCartItem({
          ...summaryData,
          bundle: selectedValues.bundle,
        });
      } else {
        await addStarlinkCartItem({
          ...summaryData,
          bundle: selectedValues.bundle,
          isOtherOption: true,
          graniteInstall: selectedValues.installation_type === 'granite',
        });
      }
    }, [
      addStarlinkCartItem,
      isStarlinkItemAlreadyInCart,
      removeStarlinkCartItem,
      selectedValues,
      summaryData,
    ]);

    const isAddedToCart = isStarlinkItemAlreadyInCart({
      ...summaryData,
      bundle: selectedValues.bundle,
    });

    const [isExpanded, setIsExpanded] = useState(false);

    const pricing = summaryData?.formal_starlink_pricing;
    const responsiveLabelShrink =
      '[&>span:first-of-type]:max-sm:text-xs [&>span:first-of-type]:md:text-[13.5px] [&>span:first-of-type]:xl:text-base flex-row';

    const totalMRC = pricing?.total_mrc ?? 0;

    const installationPrice =
      selectedValues.installation_type === 'granite'
        ? pricing?.installation ?? 0
        : 0;

    const bundlePrice =
      selectedValues.bundle === 'hperf_bundle'
        ? pricing?.hperf_bundle ?? 0
        : pricing?.enterprise_bundle ?? 0;

    const totalNRC =
      (pricing?.activation ?? 0) + bundlePrice + installationPrice;

    const fieldBackgroundClassName = useMemo(
      () =>
        isAddedToCart
          ? 'select-background-base-surface-1-pinned'
          : 'select-background-base-surface-1',
      [isAddedToCart],
    );

    return (
      <div
        className={clsx(
          'relative flex h-full max-h-full w-full flex-col gap-4 p-6 sm:w-1/2',
          isAddedToCart && 'added-to-cart-service',
          isPinnedSummary && '!w-full',
        )}
      >
        <div className="flex flex-col justify-between gap-6 lg:flex-row xl:flex-wrap">
          <p className="flex max-w-max flex-col justify-center font-bold">
            <span
              className="group flex h-[38px] cursor-pointer items-center gap-1"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span className="text-[28px] text-content-base-default">
                {formatCurrency(totalMRC)}
              </span>
              <span className="self-center text-xl text-content-base-subdued">
                /month
              </span>
              <IconButton
                icon="chevronDown"
                className={clsx(
                  isExpanded && 'rotate-180',
                  'group-hover:text-content-accent-default',
                )}
                buttonType="ghost"
                color="currentColor"
                type="button"
              />
            </span>
            <span className="text-xs">{`+${formatCurrency(
              totalNRC,
            )} in one-time fees*`}</span>
          </p>
          <div className="flex w-full max-w-[190px] flex-col gap-2 lg:self-end">
            <AddToCartButton
              isSelected={isAddedToCart}
              onAddToCart={handleAddToCart}
              disabled={false}
            />
            <GraniteButton
              variant="secondary"
              size="medium"
              className="hidden md:flex"
              onClick={(e) => {
                e.stopPropagation();
                isPinnedSummary ? handleUnpin() : handlePin();
              }}
            >
              {!isPinnedSummary ? 'Pin to compare' : 'Unpin'}
              <Pin color="inherit" />
            </GraniteButton>
          </div>
        </div>
        <motion.div
          key="other-options"
          initial={{ height: 0, opacity: 0, marginTop: 0 }}
          animate={{
            height: isExpanded ? 'auto' : 0,
            opacity: isExpanded ? 1 : 0,
            marginTop: isExpanded ? '8px' : 0,
            transitionEnd: {
              overflow: isExpanded ? 'visible' : 'hidden',
            },
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className={clsx(
            'flex w-full flex-col gap-4',
            isExpanded && 'pointer-events-auto relative z-10',
          )}
        >
          <StarlinkServiceSummary
            data={summaryData}
            isPinnedSummary={isPinnedSummary}
            graniteInstall={selectedValues.installation_type === 'granite'}
          />
        </motion.div>
        <motion.div
          initial={{ y: 0, height: 0 }}
          animate={{ y: isExpanded ? 250 : 0, height: 'auto' }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="w-full"
        >
          <Divider />
          {isExpanded && selectedValues.installation_type === 'granite' && (
            <span className="text-xs text-content-base-subdued">
              Install fee subject to change*
            </span>
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 1 }}
          animate={{
            opacity: isExpanded ? 0 : 1,
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className={clsx('w-full', isExpanded && 'pointer-events-none z-0')}
        >
          <div className={clsx('grid grid-cols-1 gap-6')}>
            <GraniteLabel
              label="Main service plan"
              element="div"
              className={responsiveLabelShrink}
            >
              <GraniteSelect
                className={clsx('w-full', fieldBackgroundClassName)}
                onChange={(selectedOption) =>
                  handleSelectChange(
                    'service_level_tier',
                    selectedOption?.value || '',
                  )
                }
                value={
                  selectedValues.service_level_tier
                    ? mainServiceOptions.find(
                        (item) =>
                          item.value === selectedValues.service_level_tier,
                      )
                    : null
                }
                name="service_level_tier"
                options={mainServiceOptions}
                variant="blue"
              />
            </GraniteLabel>
            <GraniteLabel
              element="div"
              className={clsx(responsiveLabelShrink, 'relative')}
              rightContentClassName="!top-0 right-0 pointer-events-auto"
              label="Equipment bundle"
              rightContent={
                <FieldTooltip title="Equipment bundle" className="!w-[328px]">
                  <ul className="ml-[22px] list-outside list-disc text-left text-sm text-content-base-subdued">
                    <li className="pl-0">
                      <span className="font-bold">Enterprise Kit</span>:
                      High-speed connectivity for fixed site enterprise
                      networks.
                    </li>
                    <li className="pl-0">
                      <span className="font-bold">Mini Kit</span>: Compact,
                      portable internet ideal for travel and remote work.
                    </li>
                    <li className="pl-0">
                      <span className="font-bold">High Performance Kit:</span>{' '}
                      Reliable connectivity for fixed sites with extreme climate
                      conditions including snow, rain, dust, and wind.
                    </li>
                  </ul>
                </FieldTooltip>
              }
            >
              <GraniteSelect
                className={clsx('w-full', fieldBackgroundClassName)}
                onChange={(selectedOption) =>
                  handleSelectChange('bundle', selectedOption?.value || '')
                }
                value={
                  selectedValues.bundle
                    ? equipmentOptions.find(
                        (item) => item.value === selectedValues.bundle,
                      )
                    : null
                }
                name="bundle"
                options={equipmentOptions}
                variant="blue"
              />
            </GraniteLabel>
            <GraniteLabel
              label="Install type"
              element="div"
              className={clsx(responsiveLabelShrink, 'relative')}
              rightContentClassName="!top-0 right-0 pointer-events-auto"
              rightContent={
                <div className="z-10 cursor-pointer">
                  <FieldTooltip title="Install type" className="!w-[328px]">
                    <p className="text-left text-sm text-content-base-subdued">
                      Granite install fee ($750) includes: 1) a basic site
                      survey up to 2 hours onsite; and 2) Starlink installation
                      up to 3 hours onsite including travel up to 50 miles.
                      Additional fees may apply.
                    </p>
                  </FieldTooltip>
                </div>
              }
            >
              <div className="flex gap-6">
                <RadioButton
                  options={[{ value: 'granite', label: 'Granite install' }]}
                  selectedValue={selectedValues.installation_type}
                  onChange={() =>
                    handleSelectChange('installation_type', 'granite')
                  }
                  className="mb-2"
                  variant="blue"
                />
                <RadioButton
                  options={[{ value: 'self', label: 'Self-install' }]}
                  selectedValue={selectedValues.installation_type}
                  onChange={() =>
                    handleSelectChange('installation_type', 'self')
                  }
                  variant="blue"
                />
              </div>
            </GraniteLabel>
          </div>
        </motion.div>
      </div>
    );
  });
